/**
 * Copy CSV content to the clipboard as plain text. Mirrors the milestone "paste into Excel" flow —
 * pasting the clipboard into Excel, Google Sheets, or LibreOffice produces a populated table
 * without the user ever downloading a file.
 *
 * Notes:
 * - Tab-delimited (TSV) output pastes most cleanly into spreadsheet apps; pass `delimiter: '\t'`
 *   when the primary intent is paste-into-Excel rather than a pure CSV artefact.
 * - Requires a secure context (HTTPS or localhost) and a user gesture (e.g. button click). If the
 *   page doesn't satisfy those, `navigator.clipboard.writeText` will reject.
 */

import { buildCsv } from './buildCsv';
import type { BuildCsvOptions, ColumnDef } from './types';

export type CopyCsvOptions = BuildCsvOptions;

export async function copyCsvToClipboard<TRow>(
  rows: readonly TRow[],
  columns: readonly ColumnDef<TRow>[],
  options: CopyCsvOptions = {}
): Promise<void> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.clipboard ||
    typeof navigator.clipboard.writeText !== 'function'
  ) {
    throw new Error(
      'copyCsvToClipboard: clipboard API is unavailable. Requires a secure context (HTTPS or localhost).'
    );
  }

  // No BOM by default — the clipboard is consumed by an app, not by Excel's file-open parser.
  const content = buildCsv(rows, columns, options);
  await navigator.clipboard.writeText(content);
}
